import type { McpGrantStatus, McpGrantView } from "@shared/mcp/consent";
import { MCP_SCOPE_READ, MCP_SCOPE_WRITE, type McpGrantRevokeReason } from "@shared/mcp/constants";

/**
 * The words for an MCP grant on "Connected apps" and the AI apps page (ADR 0009 section 4): what
 * it may do, whether it still works, and why it ended. Typed by the closed sets, so a new revoke
 * reason does not compile without its sentence.
 */

export const GRANT_STATUS_LABELS: Record<McpGrantStatus, string> = {
  active: "Connected",
  revoked: "Disconnected",
  expired: "Expired",
};

export const GRANT_REVOKE_REASON_TEXT: Record<McpGrantRevokeReason, string> = {
  user: "Disconnected by the person who connected it.",
  admin: "Disconnected by an administrator.",
  revoke_all: "Disconnected when a platform administrator ended every connection.",
  refresh_reuse: "Ended for safety: one of its sign-in tokens was used twice.",
  code_reuse: "Ended for safety: its approval code was used twice.",
  user_deactivated: "Ended when the account was deactivated.",
  access_lost: "Ended when the person's access to the instance ended.",
  instance_disabled: "Ended when the instance was disabled.",
  tenant_disabled: "Ended when the tenant was disabled.",
  issuer_removed: "Ended when its sign-in provider was removed.",
  switched_off: "Ended when AI apps were turned off.",
  reconsent: "Replaced by a newer approval.",
};

/** "Read", "Read and change": what the grant's scopes let the app do. */
export function grantAccessWords(scopes: readonly string[]): string {
  const read = scopes.includes(MCP_SCOPE_READ);
  const write = scopes.includes(MCP_SCOPE_WRITE);
  if (read && write) return "Read and change";
  if (write) return "Change";
  return read ? "Read" : "Nothing";
}

/**
 * The scopes themselves, under the words (release 8B): "skydata.read, skydata.write". The
 * refresh-token scope says nothing about what the app may do, so it is left out.
 */
export function grantScopeNames(scopes: readonly string[]): string {
  const shown = [MCP_SCOPE_READ, MCP_SCOPE_WRITE].filter((scope) => scopes.includes(scope));
  return shown.length > 0 ? shown.join(", ") : "-";
}

/** The sentence under a grant's status: why it ended, or when it will. */
export function grantStatusDetail(grant: McpGrantView): string | null {
  if (grant.status === "revoked") {
    return grant.revokedReason ? GRANT_REVOKE_REASON_TEXT[grant.revokedReason] : null;
  }
  if (grant.status === "expired") return "Its 30 days ran out. Connect it again from the app.";
  return null;
}
